import { lazy, Suspense, type ReactNode, useCallback } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClerkProvider, SignedIn, SignedOut, SignOutButton, useUser } from '@clerk/clerk-react';
import { MotionConfig } from 'motion/react';
import { Route, Switch, useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout';
import { CommandPalette } from '@/components/command-palette';
import { IdleLogout } from '@/components/idle-logout';
import { ErrorBoundary } from '@/components/error-boundary';
import { PageTransition } from '@/components/page-transition';
import { Button } from '@/components/ui';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import LoginPage from '@/pages/login';
import { ClerkSignInPage, ClerkSignUpPage } from '@/pages/clerk-auth';
import { api, ApiError } from '@/lib/api';
import { clerkEnabled, clerkKey } from '@/lib/clerk';
import { useAutoUpdate } from '@/hooks/use-auto-update';
import { toast } from '@/lib/toast';

const Dashboard = lazy(() => import('@/pages/dashboard'));
const Inbox = lazy(() => import('@/pages/inbox'));
const Customers = lazy(() => import('@/pages/customers'));
const CustomerForm = lazy(() => import('@/pages/customer-form'));
const Contacts = lazy(() => import('@/pages/contacts'));
const Leads = lazy(() => import('@/pages/leads'));
const LeadForm = lazy(() => import('@/pages/lead-form'));
const CalendarPage = lazy(() => import('@/pages/calendar'));
const Tasks = lazy(() => import('@/pages/tasks'));
const TaskForm = lazy(() => import('@/pages/task-form'));
const Projects = lazy(() => import('@/pages/projects'));
const ProjectForm = lazy(() => import('@/pages/project-form'));
const Invoices = lazy(() => import('@/pages/invoices'));
const InvoiceForm = lazy(() => import('@/pages/invoice-form'));
const Payments = lazy(() => import('@/pages/payments'));
const VendorAdvices = lazy(() => import('@/pages/vendor-advices'));
const RevenueArchive = lazy(() => import('@/pages/revenue'));
const Revenue2 = lazy(() => import('@/pages/revenue2'));
const Recruitment = lazy(() => import('@/pages/recruitment'));
const Onboarding = lazy(() => import('@/pages/onboarding'));
const Assets = lazy(() => import('@/pages/assets'));
const AssetForm = lazy(() => import('@/pages/asset-form'));
const DocumentManagement = lazy(() => import('@/pages/document-management'));
const Reports = lazy(() => import('@/pages/reports'));
const InvoiceAnalytics = lazy(() => import('@/pages/invoice-analytics'));
const ResourceBilling = lazy(() => import('@/pages/resource-billing'));
const Settings = lazy(() => import('@/pages/settings'));
const Users = lazy(() => import('@/pages/users'));

/** A 401 mid-session means the cookie expired (e.g. 1h inactivity): re-check
 *  /api/me so the auth boundary swaps the app for the login page. */
function onUnauthorized(sourceKey?: readonly unknown[]) {
  // Never re-trigger from the me query itself — that would refetch-loop.
  if (sourceKey?.[0] === 'me') return;
  void queryClient.invalidateQueries({ queryKey: ['me'] });
}

const queryClient = new QueryClient({
  // Surface failed reads as a toast instead of silently rendering empty states.
  // 401s are the auth boundary's job (login prompt), so skip the toast.
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized(query.queryKey);
        return;
      }
      const msg = err instanceof ApiError ? err.message : 'Something went wrong loading data';
      toast.error(msg, { id: `query:${msg}` });
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) onUnauthorized();
    },
  }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});


/**
 * Fetch the server-side auth mode once. Consumers share the same cached result
 * so there is only one network call per session.
 */
export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth', 'config'],
    queryFn: () => api.get<{ mode: string }>('/api/auth/config'),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Guards all app routes: shows a login page when the /api/me call returns 401,
 * and re-renders the app after a successful sign-in.
 */
function LocalAuthBoundary({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data: me, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const handleSuccess = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  if (isError || !me) {
    return <LoginPage onSuccess={handleSuccess} />;
  }

  return <>{children}</>;
}

/** Clerk users without an admin-invited CRM account (403 from /api/me) see
 *  guidance instead of a wall of failed queries. */
export function ProvisionBoundary({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const { isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  if (error instanceof ApiError && error.status === 403) {
    const email = user?.primaryEmailAddress?.emailAddress;
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md space-y-3 text-center">
          <h1 className="text-lg font-semibold">Your account isn&apos;t set up yet</h1>
          <p className="text-sm text-muted-foreground">
            Ask your administrator to invite {email ? <strong>{email}</strong> : 'you'} from the
            Users page, then sign in again.
          </p>
          <SignOutButton>
            <Button variant="outline">Sign out</Button>
          </SignOutButton>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/** Clerk wraps the app when real keys exist; otherwise LocalAuthBoundary handles
 *  dev (always passes) and local (cookie JWT) modes. */
function AuthBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  if (clerkEnabled) {
    return (
      <ClerkProvider publishableKey={clerkKey!}>
        <SignedIn>
          <ProvisionBoundary>{children}</ProvisionBoundary>
        </SignedIn>
        <SignedOut>
          {location === '/sign-up' ? <ClerkSignUpPage /> : <ClerkSignInPage />}
        </SignedOut>
      </ClerkProvider>
    );
  }
  return <LocalAuthBoundary>{children}</LocalAuthBoundary>;
}

/** Suspense fallback: fades in after a short delay so fast loads never flash. */
function PageLoader() {
  return (
    <div className="flex justify-center py-24">
      <div className="flex items-center gap-2 text-sm text-muted-foreground animate-in fade-in fill-mode-both delay-150 duration-300">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading…
      </div>
    </div>
  );
}

function Placeholder({ title }: { title: string }) {
  return (
    <div>
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">Module under construction.</p>
    </div>
  );
}

export default function App() {
  useAutoUpdate();
  return (
    <MotionConfig reducedMotion="user">
    <QueryClientProvider client={queryClient}>
      <AuthBoundary>
        <TooltipProvider>
        <IdleLogout />
        <CommandPalette />
        <Toaster position="bottom-right" />
        <Layout>
          <ErrorBoundary>
          <PageTransition>
          <Suspense fallback={<PageLoader />}>
            <Switch>
              <Route path="/" component={Dashboard} />
              <Route path="/inbox" component={Inbox} />
              <Route path="/customers" component={Customers} />
              <Route path="/customers/new" component={CustomerForm} />
              <Route path="/customers/:id/edit" component={CustomerForm} />
              <Route path="/contacts" component={Contacts} />
              <Route path="/leads" component={Leads} />
              <Route path="/leads/new" component={LeadForm} />
              <Route path="/leads/:id/edit" component={LeadForm} />
              <Route path="/calendar" component={CalendarPage} />
              <Route path="/tasks" component={Tasks} />
              <Route path="/tasks/new" component={TaskForm} />
              <Route path="/tasks/:id/edit" component={TaskForm} />
              <Route path="/projects" component={Projects} />
              <Route path="/projects/new" component={ProjectForm} />
              <Route path="/projects/:id/edit" component={ProjectForm} />
              <Route path="/invoices" component={Invoices} />
              <Route path="/invoices/new" component={InvoiceForm} />
              <Route path="/invoices/:id/edit" component={InvoiceForm} />
              <Route path="/payments" component={Payments} />
              <Route path="/vendor-advices" component={VendorAdvices} />
              <Route path="/revenue" component={RevenueArchive} />
              <Route path="/revenue2" component={Revenue2} />
              <Route path="/recruitment" component={Recruitment} />
              <Route path="/onboarding" component={Onboarding} />
              <Route path="/assets" component={Assets} />
              <Route path="/assets/new" component={AssetForm} />
              <Route path="/assets/:id" component={AssetForm} />
              <Route path="/documents" component={DocumentManagement} />
              <Route path="/reports" component={Reports} />
              <Route path="/invoice-analytics" component={InvoiceAnalytics} />
              <Route path="/resource-billing" component={ResourceBilling} />
              <Route path="/settings/users" component={Users} />
              <Route path="/settings" component={Settings} />
              <Route>
                <Placeholder title="Not found" />
              </Route>
            </Switch>
          </Suspense>
          </PageTransition>
          </ErrorBoundary>
        </Layout>
        </TooltipProvider>
      </AuthBoundary>
    </QueryClientProvider>
    </MotionConfig>
  );
}
